import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../../context/useAuth';
import Breadcrumbs, { BreadcrumbItem } from '../../common/Breadcrumbs';
import Logout from '../../auth/Logout';
import { useTeamStore } from '../../../lib/store/teamStore';

// Navigation items
const navigationItems = [
  { name: 'Dashboard', href: '/dashboard', icon: 'HomeIcon' },
  { name: 'Teams', href: '/teams', icon: 'UsersIcon' },
  { name: 'Schedules', href: '/schedules', icon: 'CalendarIcon' },
  { name: 'Settings', href: '/settings', icon: 'CogIcon' },
];

// Icon components
const icons: Record<string, React.FC> = {
  HomeIcon: () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  ),
  CalendarIcon: () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  UsersIcon: () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  CogIcon: () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
};

const AppLayout: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { teams, currentTeam, setCurrentTeam } = useTeamStore();
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  
  // Toggle sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Generate breadcrumbs based on current path
  useEffect(() => {
    const generateBreadcrumbs = async () => {
      // Always include home
      const newBreadcrumbs: BreadcrumbItem[] = [
        { label: 'Home', href: '/dashboard' }
      ];
      
      // Skip if we're already on the dashboard
      if (location.pathname === '/dashboard') {
        setBreadcrumbs(newBreadcrumbs);
        return;
      }
      
      // Split the path into segments and build breadcrumbs
      const segments = location.pathname.split('/').filter(Boolean);
      
      // UUID regex pattern - match both hyphenated and non-hyphenated formats
      const uuidPattern = /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;
      
      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        let label = segment.replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase());
        const href = `/${segments.slice(0, i + 1).join('/')}`;
        
        // Check if this segment matches a UUID pattern (with or without hyphens)
        if (uuidPattern.test(segment)) {
          // This is likely an ID (team, schedule, etc.)
          if (i > 0 && segments[i-1] === 'teams') {
            // If it's a team ID, check if we have the team in store
            const team = teams.find(t => t.id === segment);
            
            if (team) {
              // Use team name as the label
              label = team.name;
            } else if (currentTeam && currentTeam.id === segment) {
              // Use current team name if available
              label = currentTeam.name;
            } else {
              // Set a temporary label and try to fetch the team
              label = 'Team';
              setCurrentTeam(segment);
            }
          } else {
            // For other IDs (like schedules), use generic labels
            if (i > 0) {
              // Use the previous segment to determine the type
              const prevSegment = segments[i-1];
              if (prevSegment === 'schedules') {
                label = 'Schedule';
              } else if (prevSegment === 'members') {
                label = 'Member';
              } else {
                label = 'Item';
              }
            } else {
              label = 'Item';
            }
          }
        }
        
        newBreadcrumbs.push({ label, href });
      }
      
      setBreadcrumbs(newBreadcrumbs);
    };
    
    generateBreadcrumbs();
  }, [location.pathname, teams, currentTeam, setCurrentTeam]);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside 
        className={`bg-white shadow-sm transition-all duration-300 ${
          isSidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-center border-b">
          <h1 className={`text-2xl font-bold text-primary ${isSidebarOpen ? 'block' : 'hidden'}`}>
            ShiftNapse
          </h1>
          <h1 className={`text-2xl font-bold text-primary ${isSidebarOpen ? 'hidden' : 'block'}`}>
            SN
          </h1>
        </div>
        
        {/* Navigation */}
        <nav className="mt-6 px-3">
          <ul className="space-y-2">
            {navigationItems.map((item) => {
              const Icon = icons[item.icon];
              const isActive = location.pathname === item.href;
              
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`flex items-center py-2 px-3 rounded-md ${
                      isActive 
                        ? 'bg-primary-50 text-primary'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon />
                    {isSidebarOpen && (
                      <span className="ml-3">{item.name}</span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        
        {/* Sidebar toggle */}
        <button
          className="absolute bottom-4 left-0 right-0 mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white"
          onClick={toggleSidebar}
          aria-label={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-5 w-5 transition-transform ${isSidebarOpen ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </aside>
      
      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm z-10">
          <div className="px-4 py-4 sm:px-6 lg:px-8 flex items-center justify-between">
            {/* Breadcrumbs */}
            <Breadcrumbs items={breadcrumbs} />
            
            {/* User menu */}
            <div className="flex items-center space-x-4">
              <div className="text-sm flex items-center">
                <span className="text-gray-700">Welcome, {user?.email}</span>
                <div className="mx-2 h-4 w-px bg-gray-200"></div>
                <Link to="/profile" className="text-gray-700 hover:text-primary mr-2">
                  My Profile
                </Link>
                <div className="mx-2 h-4 w-px bg-gray-200"></div>
                <Logout className="text-sm" />
              </div>
            </div>
          </div>
        </header>
        
        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
